import { Component, OnInit } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Select } from '@ngxs/store';
import { SelectedMatter } from '@state/matter/matter.selectors';
import { Observable } from 'rxjs';
import { ICON } from '@core/util/icon';
import { MatterDispatch } from '@state/matter';
import { SelectSnapshot } from '@ngxs-labs/select-snapshot';
import {
	MatterManualTitleInsurance,
	TitleInsuranceProvider,
} from '@models/title-insurance';
import { openClose } from '@core/animations/animations';
import { isSet } from '@core/util/value';
import {
	DropdownResult,
	DropdownOption,
} from 'src/app/shared/atomic/atoms/dropdown-options/dropdown-options.component';
import { RadioGroupItem } from 'src/app/shared/atomic/atoms/radio-group/radio-group.component';
import {
	DirectionOfFeeString,
	DirectionOfFeeType,
} from '@models/businessSettings/directionOfFees';
import {
	AnalyticsEvent,
	AnalyticsService,
} from '@services/analytics/analytics.service';
import { DirectionOfAmountSettings } from '@models/businessSettings/settings';

export const DisplayOptions = [
	{
		name: DirectionOfFeeString[DirectionOfFeeType.TRUST_LEDGER],
		value: DirectionOfFeeType.TRUST_LEDGER,
	},
	{
		name: DirectionOfFeeString[DirectionOfFeeType.SOA],
		value: DirectionOfFeeType.SOA,
	},
	{
		name: DirectionOfFeeString[DirectionOfFeeType.NONE],
		value: DirectionOfFeeType.NONE,
	},
];

export const ExtendOptions: RadioGroupItem<boolean>[] = [
	{
		label: 'Yes',
		value: true,
	},
	{
		label: 'No',
		value: false,
	},
];

@UntilDestroy()
@Component({
	selector: 'app-finance-settings',
	templateUrl: './finance-settings.component.html',
	animations: [openClose(300)],
	standalone: false,
})
export class FinanceSettingsComponent implements OnInit {
	public ICON = ICON;
	public displayOptions = [{ items: DisplayOptions }];
	public DirectionOfFeeType = DirectionOfFeeType;
	public location:
		| DirectionOfFeeType.TRUST_LEDGER
		| DirectionOfFeeType.SOA
		| DirectionOfFeeType.NONE = DirectionOfFeeType.TRUST_LEDGER;
	public directionOfFeeString = DirectionOfFeeString;
	public extendToClientOptions = ExtendOptions;
	public combineCounselFee = false;
	public extendCreditToClient = false;
	public hidePremium = false;
	public message = '';
	public showDefaultInfoMessage = false;
	public disableCounselSettings = false;
	public subjectToHSTInSOA = false;
	public provider: TitleInsuranceProvider;
	public DOAsettings: DirectionOfAmountSettings;
	showCounselCredit = false;

	@Select(SelectedMatter.getTitleInsurance)
	titleInsurance$: Observable<MatterManualTitleInsurance>;
	@SelectSnapshot(SelectedMatter.id) matterId: string;
	@SelectSnapshot(SelectedMatter.getTitleInsuranceDirectionOfAmount)
	titleInsuranceDOA: DirectionOfFeeType;

	constructor(
		private dialogRef: MatDialogRef<FinanceSettingsComponent>,
		private matterDispatch: MatterDispatch,
		private analytics: AnalyticsService,
	) {}

	ngOnInit(): void {
		this.titleInsurance$
			.pipe(untilDestroyed(this))
			.subscribe((_titleInsurance) => {
				this.provider = _titleInsurance.provider;
				this.combineCounselFee = !!_titleInsurance.combineCounselFee;
				this.extendCreditToClient = !!_titleInsurance.extendCreditToClient;
				this.hidePremium = this.titleInsuranceDOA === DirectionOfFeeType.NONE;
				this.DOAsettings = _titleInsurance.directionOfAmount;
				this.showCounselCredit = _titleInsurance.showCounselCredit;

				if (this.hidePremium) {
					this.location = DirectionOfFeeType.NONE;
					this.disableCounselSettings = true;
				} else {
					this.location = this.titleInsuranceDOA;
				}

				if (isSet(_titleInsurance.subjectToHSTInSOA)) {
					this.subjectToHSTInSOA = !!_titleInsurance.subjectToHSTInSOA;
				} else {
					this.subjectToHSTInSOA =
						!!_titleInsurance.directionOfAmount.subjectToHSTInSOA;
				}
			});
	}

	close() {
		this.dialogRef.close();
	}

	setExtendCredit(extendCreditToClient: boolean) {
		this.extendCreditToClient = extendCreditToClient;

		if (extendCreditToClient) {
			this.setCombineCounselFee(false);
			this.showCounselCredit = false;
		}
	}

	setCombineCounselFee(combineCounselFee: boolean) {
		this.combineCounselFee = combineCounselFee;

		if (combineCounselFee) {
			this.showCounselCredit = false;
		}
	}

	setDirectionOfAmountLocation(
		location: DropdownResult<
			DropdownOption<
				| DirectionOfFeeType.TRUST_LEDGER
				| DirectionOfFeeType.SOA
				| DirectionOfFeeType.NONE
			>
		>,
	) {
		this.hidePremium = location.result.value === DirectionOfFeeType.NONE;
		this.disableCounselSettings =
			location.result.value === DirectionOfFeeType.NONE;
		this.location = location.result.value;

		if (location.result.value === DirectionOfFeeType.NONE) {
			this.showCounselCredit = false;
		}
	}

	setShowCounselCredit(showCounselCredit: boolean) {
		this.showCounselCredit = showCounselCredit;
	}

	save() {
		this.analytics.logEvent(
			AnalyticsEvent.TITLE_INSURANCE_FINANCE_SETTINGS_OVERRIDDEN,
			{
				provider: this.provider || 'N/A',
			},
		);

		this.matterDispatch.SetTitleInsurance(this.matterId, {
			extendCreditToClient: this.extendCreditToClient,
			combineCounselFee: this.combineCounselFee,
			directionOfAmount: {
				...this.DOAsettings,
				location: this.location,
				subjectToHSTInSOA: this.subjectToHSTInSOA,
			},
			subjectToHSTInSOA: this.subjectToHSTInSOA,
			showCounselCredit: this.showCounselCredit,
		});
		this.dialogRef.close();
	}

	toggleShowDefaultInfoMessage() {
		this.showDefaultInfoMessage = !this.showDefaultInfoMessage;
	}
}
